<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Аукционы</div>

            <div class="content-header__actions">
                <router-link v-if="canCreateAuction()" :to="{ name: 'CreateAuction' }">
                    <UISimpleButton>
                        <template #icon>
                            <img src="@/assets/icons/ui/plus-white.svg" />
                        </template>
                        Создать аукцион
                    </UISimpleButton>
                </router-link>
            </div>

            <div class="content-header__background content-header__background--3-lines-filters auction-list-header__background"></div>
        </div>

        <!-- Панель фильтров -->
        <FilterPanel
            :columns="4"
            @search="onSearch"
            @reset="onReset"
        >
            <template #actions-left>
                <UIToggle
                    v-if="!isAdmin()"
                    v-model="auctionsFilterJoining"
                    label="Я участвую"
                />

                <UIToggle
                    v-if="isAdmin() || isCertifiedDealer()"
                    v-model="auctionsFilterConfirmationRequired"
                    label="Требуется подтверждение"
                />
            </template>

            <UISelect
                id="status"
                v-model="auctionsFilter.status"
                :options="statusOptions"
                label="Статус"
                multiple
            />

            <UISelect
                v-if="isAdmin()"
                id="city_id"
                v-model="auctionsFilter.city_ids"
                :options="cityOptions"
                label="Город"
                multiple
                searchable
            />

            <UISelect
                v-if="isAdmin()"
                id="dealer_id"
                v-model="auctionsFilter.dealer_ids"
                :options="dealerOptions"
                label="Дилер"
                multiple
                searchable
            />

            <UISelect
                id="brand_id"
                v-model="auctionsFilterBrandId"
                :options="brandOptions"
                label="Марка"
            />

            <UISelect
                id="model_id"
                v-model="auctionsFilter.model_ids"
                :options="modelOptions"
                label="Модель"
                multiple
                searchable
            />

            <UISelect
                id="transmission_type"
                v-model="auctionsFilter.transmission_type"
                :options="transmissionOptions"
                label="Тип трансмиссии"
                multiple
            />

            <UIDatePicker
                v-model="auctionsFilter.from_date"
                label="Дата от"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UIDatePicker
                v-model="auctionsFilter.to_date"
                label="Дата до"
                :options="{ enableTime: false, dateFormat: 'd.m.Y' }"
            />

            <UIRange
                v-model="auctionsFilter.production_year"
                :min="CarMinProductionYear"
                :max="CarMaxProductionYear"
                :interval="1"
                label="Год выпуска"
                :format-numbers="false"
            />

            <UIRange
                v-model="auctionsFilter.mileage"
                :min="0"
                :max="CarMaxMileage"
                :interval="100"
                label="Пробег"
            />

            <UIRange
                v-model="auctionsFilter.start_price"
                :min="0"
                :max="CarMaxPrice"
                :interval="CarRangePriceStep"
                label="Цена"
            />
        </FilterPanel>

        <UISearchResults
            :count="auctionsPaginationMeta?.pagination.total || 0"
            :initial-sort="selectedSort"
            :sort-options="sortOptions"
            @update:sort="onSortUpdate"
        />

        <UILoader v-if="isAuctionsLoading" class="listable" />

        <div v-else class="content__body">
            <div v-if="arAuctions.length > 0" class="auction-list__content">
                <AuctionListItem
                    v-for="auction in arAuctions"
                    :key="'auction.' + auction.id"
                    :auction="auction"
                    @updated="fetchAuctions"
                />
            </div>
            <div v-else>
                <UINotFoundData />
            </div>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="auctionsPaginationMeta?.pagination.last_page ?? 1"
                :current-page="auctionsPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="auctionsPaginationMeta?.pagination.per_page ?? 10"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref } from 'vue';

    import { SelectOption, SortOption } from '@/types/form.type.ts';
    import {
        Auction,
        AuctionsRequestFilter,
        AuctionStatus,
        InProgressAuctionBroadcastItem,
        getAuctionStatusSelectOptions,
    } from '@/types/auction.type.ts';
    import {
        getCarTransmissionTypeSelectOptions,
        CarMinProductionYear,
        CarMaxProductionYear,
        CarMaxPrice,
        CarMaxMileage,
        CarRangePriceStep,
    } from '@/types/car.type.ts';
    import { City } from '@/types/city.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import { Model } from '@/types/model.type.ts';

    import useAuctionBroadcaster from '@/repositories/auction/useAuctionBroadcaster.ts';
    import useAuctionRepository from '@/repositories/auction/useAuctionRepository';
    import useCityRepository from '@/repositories/city/useCityRepository.ts';
    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useModelRepository from '@/repositories/model/useModelRepository.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';
    import { useAuctionsFilterStore } from '@/stores/auctions/auctions-filter.ts';

    import { getBrandSelectOptions } from '@/utils/SelectOptionFillers.ts';
    import { formatToUTC } from '@/utils/DateTimeFormatter.ts';

    import UIDatePicker from '@/components/ui/UIDatePicker/UIDatePicker.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import UINotFoundData from '@/components/ui/UINotFoundData/UINotFoundData.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';
    import UISearchResults from '@/components/ui/UISearchResults/UISearchResults.vue';
    import UISimpleButton from '@/components/ui/UISimpleButton/UISimpleButton.vue';
    import UIRange from '@/components/ui/UIRange/UIRange.vue';
    import UIToggle from '@/components/ui/UIToggle/UIToggle.vue';

    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import AuctionListItem from '@/components/auction/AuctionListItem/AuctionListItem.vue';

    const { arAuctions, auctionsPaginationMeta, isAuctionsLoading, getAuctions } = useAuctionRepository();
    const { getAllCities } = useCityRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { getAllActiveModels } = useModelRepository();
    const { isAdmin, isCertifiedDealer, canCreateAuction } = usePermission();
    const {
        listenInProgressAuctions,
        leaveInProgressAuctions,
        listenAuctionStatus,
        leaveAuctionStatus,
    } = useAuctionBroadcaster();
    const auctionsFilterStore = useAuctionsFilterStore();

    const auctionsFilter = ref<AuctionsRequestFilter>({ ...auctionsFilterStore.$state });
    const brandOptions = ref<SelectOption[]>(getBrandSelectOptions());
    const cityOptions = ref<SelectOption[]>([]);
    const dealerOptions = ref<SelectOption[]>([]);
    const statusOptions = ref<SelectOption[]>(getAuctionStatusSelectOptions(isAdmin()));
    const transmissionOptions = ref<SelectOption[]>(getCarTransmissionTypeSelectOptions());
    const selectedSort = ref('start_date|desc');
    const sortOptions = ref<SortOption[]>([
        { label: 'по дате начала (1-9)', value: 'start_date|asc' },
        { label: 'по дате начала (9-1)', value: 'start_date|desc' },
        { label: 'по дате окончания (1-9)', value: 'end_date|asc' },
        { label: 'по дате окончания (9-1)', value: 'end_date|desc' },
        { label: 'по стартовой цене (1-9)', value: 'start_price|asc' },
        { label: 'по стартовой цене (9-1)', value: 'start_price|desc' },
    ]);

    const arModels = ref<Model[]>([]);

    const auctionsFilterBrandId = computed({
        get() {
            return auctionsFilter.value.brand_ids === undefined || auctionsFilter.value.brand_ids.length === 0
                ? undefined
                : auctionsFilter.value.brand_ids[0];
        },
        set(brandId?: number) {
            auctionsFilter.value.brand_ids = brandId === undefined ? undefined : [brandId];
        },
    });

    const auctionsFilterJoining = computed({
        get() {
            return auctionsFilter.value.joining === 1;
        },
        set(joining: boolean) {
            auctionsFilter.value.joining = joining ? 1 : 0;
        },
    });

    const auctionsFilterConfirmationRequired = computed({
        get() {
            return auctionsFilter.value.confirmation_required === 1;
        },
        set(confirmation_required: boolean) {
            auctionsFilter.value.confirmation_required = confirmation_required ? 1 : 0;
        },
    });

    const modelOptions = computed<SelectOption[]>(() => {
        return arModels.value
            .filter((model: Model) => {
                if (auctionsFilter.value.brand_ids === undefined) {
                    return true;
                }

                if (auctionsFilter.value.brand_ids.length === 0) {
                    return true;
                }

                return auctionsFilter.value.brand_ids.includes(model.brand_id);
            })
            .map((model: Model) => ({
                label: model.name,
                value: model.id,
            }));
    });

    function onSearch(): void {
        auctionsFilter.value.page = 1;
        fetchAuctions();
    }

    function onReset(): void {
        auctionsFilter.value = {
            brand_ids: undefined,
            model_ids: undefined,
            from_date: undefined,
            to_date: undefined,
            status: [AuctionStatus.InProgress],
            start_price: [0, CarMaxPrice],
            transmission_type: undefined,
            mileage: [0, CarMaxMileage],
            production_year: [CarMinProductionYear, CarMaxProductionYear],
            city_ids: undefined,
            dealer_ids: undefined,
            confirmation_required: undefined,
            joining: undefined,
            sort: auctionsFilter.value.sort,
            page: 1,
        };

        fetchAuctions();
    }

    function changePage(page: number): void {
        auctionsFilter.value.page = page;
        fetchAuctions();
    }

    function onSortUpdate(newSort: string): void {
        const parts = newSort.split('|');

        if (parts.length !== 2) {
            return;
        }

        const [key, dir] = parts as [string, 'asc' | 'desc'];

        auctionsFilter.value.sort = [{ [key]: dir }];
        fetchAuctions();
    }

    function onInProgressAuctionsBroadcast(auctions: InProgressAuctionBroadcastItem[]): void {
        auctions.forEach((auctionInfo: InProgressAuctionBroadcastItem) => {
            const auction = arAuctions.value.find((auction: Auction) => auction.id === auctionInfo.lot_id);
            if (auction === undefined) {
                return;
            }

            auction.broadcastInfo = auctionInfo;
        });
    }

    function onAuctionStatusBroadcast(): void {
        fetchAuctions();
    }

    function mapAuctionsFilterToRequestFilter(): AuctionsRequestFilter {
        const requestFilter: AuctionsRequestFilter = {
            start_price: auctionsFilter.value.start_price,
            mileage: auctionsFilter.value.mileage,
            production_year: auctionsFilter.value.production_year,
            sort: auctionsFilter.value.sort,
            page: auctionsFilter.value.page,
        };

        if (auctionsFilter.value.brand_ids !== undefined && auctionsFilter.value.brand_ids.length > 0) {
            requestFilter.brand_ids = auctionsFilter.value.brand_ids;
        }

        if (auctionsFilter.value.model_ids !== undefined && auctionsFilter.value.model_ids.length > 0) {
            requestFilter.model_ids = auctionsFilter.value.model_ids;
        }

        if (auctionsFilter.value.from_date !== undefined && auctionsFilter.value.from_date.length > 0) {
            requestFilter.from_date = formatToUTC(auctionsFilter.value.from_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        if (auctionsFilter.value.to_date !== undefined && auctionsFilter.value.to_date.length > 0) {
            requestFilter.to_date = formatToUTC(auctionsFilter.value.to_date, {
                fromFormat: 'dd.LL.yyyy',
                toFormat: 'yyyy-LL-dd',
                withTime: false,
            });
        }

        if (auctionsFilter.value.status !== undefined && auctionsFilter.value.status.length > 0) {
            requestFilter.status = auctionsFilter.value.status;
        }

        if (auctionsFilter.value.transmission_type !== undefined && auctionsFilter.value.transmission_type.length > 0) {
            requestFilter.transmission_type = auctionsFilter.value.transmission_type;
        }

        if (auctionsFilter.value.city_ids !== undefined && auctionsFilter.value.city_ids.length > 0) {
            requestFilter.city_ids = auctionsFilter.value.city_ids;
        }

        if (auctionsFilter.value.dealer_ids !== undefined && auctionsFilter.value.dealer_ids.length > 0) {
            requestFilter.dealer_ids = auctionsFilter.value.dealer_ids;
        }

        if (auctionsFilter.value.confirmation_required !== undefined) {
            requestFilter.confirmation_required = auctionsFilter.value.confirmation_required;
        }

        if (auctionsFilter.value.joining !== undefined) {
            requestFilter.joining = auctionsFilter.value.joining;
        }

        return requestFilter;
    }

    function fetchAuctions(): void {
        auctionsFilterStore.$patch({ ...auctionsFilter.value });
        getAuctions(mapAuctionsFilterToRequestFilter());
    }

    function fetchFilterSelectOptions(): void {
        getAllCities().then((cities: City[]) => {
            const options: SelectOption[] = [];
            cities.forEach((city: City) => {
                options.push({
                    label: city.name,
                    value: city.id,
                });
            });

            cityOptions.value = options;
        });

        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            const options: SelectOption[] = [];
            dealers.forEach((dealer: DealerListItem) => {
                options.push({
                    label: dealer.name + ' (' + dealer.sap_code + ')',
                    value: dealer.id,
                });
            });

            dealerOptions.value = options;
        });

        getAllActiveModels().then((models: Model[]) => {
            arModels.value = models;
        });
    }

    onMounted(() => {
        fetchAuctions();
        fetchFilterSelectOptions();
        listenInProgressAuctions(onInProgressAuctionsBroadcast);
        listenAuctionStatus(onAuctionStatusBroadcast);
    });

    onUnmounted(() => {
        leaveInProgressAuctions();
        leaveAuctionStatus();
    });
</script>

<style scoped lang="scss">
    @use './AuctionsList.scss' as *;
</style>
